import React, { useState, useEffect } from 'react';
import edit from '../../images/edit.svg';
import Select, { components } from 'react-select';
import { customStyles } from './../../helpers/StyleUtils';
import { bookingService, userService, talentService, talentGroupService } from '../../api/apiService';
import { useAuth } from '../../contexts/AuthContext';

const ListViewAllBookings = ({ events, authToken, initialWeek }) => {
    // Enhanced debug logging
    // console.log('ListViewAllBookings received events:', events ? events.length : 0, 'events');
    // console.log('First event sample:', events && events.length > 0 ? events[0] : 'No events');
    // console.log('authToken present:', !!authToken);

    const { currentUser, updateUserPreferences } = useAuth();
    
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedPosition, setSelectedPosition] = useState(null);
    const [selectedTalent, setSelectedTalent] = useState(null);
    const [selectedSet, setSelectedSet] = useState(null);
    const [isKindMode, setIsKindMode] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedTalentGroup, setSelectedTalentGroup] = useState(null);
    const [talentGroups, setTalentGroups] = useState([]);
    const [userEmail, setUserEmail] = useState(currentUser ? currentUser.email : '');
    const [filterPreferences, setFilterPreferences] = useState([]);
    const [selectedPreferenceIndex, setSelectedPreferenceIndex] = useState(null); 
    const [selectedOrders, setSelectedOrders] = useState({});
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [currentWeek, setCurrentWeek] = useState(initialWeek || getCurrentWeek());
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

    // Helper function to get current week number (ISO 8601)
    function getCurrentWeek() {
        const now = new Date();
        const start = new Date(now.getFullYear(), 0, 1);
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        // Set both dates to Thursday of their respective weeks
        // This ensures we get the correct ISO week number
        start.setDate(start.getDate() + (4 - (start.getDay() || 7)));
        today.setDate(today.getDate() + (4 - (today.getDay() || 7)));
        
        const weekNumber = Math.ceil((((today - start) / 86400000) + 1) / 7);
        return weekNumber;
    }

    // Helper function to get ISO week number
    function getISOWeek(date) {
        const target = new Date(date.valueOf());
        const dayNr = (date.getDay() + 6) % 7;
        target.setDate(target.getDate() - dayNr + 3);
        const firstThursday = target.valueOf();
        target.setMonth(0, 1);
        if (target.getDay() !== 4) {
            target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
        }
        return 1 + Math.ceil((firstThursday - target) / 604800000);
    }

    // Helper function to get dates for a specific week and year
    function getWeekDates(weekNumber, year) {
        // Get January 4th of the year (always in week 1)
        const jan4 = new Date(year, 0, 4);
        
        // Find Monday of week 1
        const mondayOfWeek1 = new Date(jan4);
        mondayOfWeek1.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7));
        
        // Calculate Monday of the target week
        const weekStart = new Date(mondayOfWeek1);
        weekStart.setDate(mondayOfWeek1.getDate() + (weekNumber - 1) * 7);
        
        // Calculate Sunday of the target week
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        
        // Debug logging
        // console.log(`Week ${weekNumber} of ${year} dates:`, {
        //     weekStart: weekStart.toLocaleDateString(),
        //     weekEnd: weekEnd.toLocaleDateString(),
        //     today: new Date().toLocaleDateString(),
        //     currentWeekNum: getCurrentWeek()
        // });
        
        return { weekStart, weekEnd, year: weekStart.getFullYear() };
    }

    // Navigation functions
    const goToPreviousWeek = () => {
        if (currentWeek === 1) {
            // Go to last week of previous year
            setCurrentYear(currentYear - 1);
            setCurrentWeek(52); // Most years have 52 weeks
        } else {
            setCurrentWeek(currentWeek - 1);
        }
    };

    const goToNextWeek = () => {
        const { weekStart } = getWeekDates(currentWeek + 1, currentYear);
        const weekYear = weekStart.getFullYear();
        
        if (weekYear > currentYear) {
            // Moving to next year
            setCurrentYear(weekYear);
            setCurrentWeek(1);
        } else if (currentWeek >= 52) {
            // Check if year has 53 weeks
            const dec31 = new Date(currentYear, 11, 31);
            const lastWeek = getISOWeek(dec31);
            if (currentWeek < lastWeek) {
                setCurrentWeek(currentWeek + 1);
            } else {
                // Go to first week of next year
                setCurrentYear(currentYear + 1);
                setCurrentWeek(1);
            }
        } else {
            setCurrentWeek(currentWeek + 1);
        }
    };

    const goToCurrentWeek = () => {
        const now = new Date();
        setCurrentWeek(getISOWeek(now));
        setCurrentYear(now.getFullYear());
    };

    // Fetch talent groups on mount
    useEffect(() => {
        const fetchTalentGroups = async () => {
            try {
                const response = await talentGroupService.getAllGroups(1, 100);
                if (response.data?.success && response.data?.data?.groups) {
                    const groups = response.data.data.groups.map(group => ({
                        id: group.id.toString(),
                        name: group.name,
                        talentIds: group.talent_ids || [],
                        createdAt: group.created_at
                    }));
                    setTalentGroups(groups);
                } else if (response.data?.data && Array.isArray(response.data.data)) {
                    const groups = response.data.data.map(group => ({
                        id: group.id.toString(),
                        name: group.name,
                        talentIds: group.talent_ids || [],
                        createdAt: group.created_at
                    }));
                    setTalentGroups(groups);
                }
            } catch (error) {
                console.error('Error loading talent groups:', error);
            }
        };

        fetchTalentGroups();
    }, []);

    // Update userEmail when currentUser changes
    useEffect(() => {
        if (currentUser && currentUser.email) {
            setUserEmail(currentUser.email);
        }
    }, [currentUser]);

    // Effect to handle KIND mode toggle
    useEffect(() => {
        if (isKindMode) {
            // When KIND mode is ON, set to "digital Production"
            setSelectedSet({ value: 'digital Production', label: 'digital Production' });
        } else {
            // When KIND mode is OFF, clear the filter
            setSelectedSet(null);
        }
    }, [isKindMode]);

    // Handle API errors
    const handleApiError = (error, operationName) => {
        console.error(`API operation error (${operationName}):`, error);
        setError(`Unable to perform ${operationName}. Please try again later or contact support.`);
        return null;
    };

    // Add debugging in the useEffect hook
    useEffect(() => {
        // console.log('Component mounted, userEmail:', userEmail);
        const fetchPreferences = async () => {
            if (!userEmail) {
                // console.log('No userEmail found, skipping preference fetch');
                return;
            }
            
            try {
                setLoading(true);
                // console.log('Fetching user preferences for:', userEmail);
                
                // First try to get from localStorage
                const localPrefs = localStorage.getItem(`filterPreferences_${userEmail}`);
                if (localPrefs) {
                    try {
                        const parsedPrefs = JSON.parse(localPrefs);
                        // console.log('Found preferences in localStorage:', parsedPrefs);
                        setFilterPreferences(parsedPrefs);
                    } catch (e) {
                        console.error('Error parsing local preferences:', e);
                    }
                }
                
                // Then try to get from API (which will override localStorage if successful)
                const response = await userService.getProfile();
                // console.log('User profile response:', response);
                
                if (response.data && response.data.filterPreferences) {
                    setFilterPreferences(response.data.filterPreferences);
                    // console.log('Filter preferences loaded from API:', response.data.filterPreferences);
                    
                    // Update localStorage with the server version
                    localStorage.setItem(`filterPreferences_${userEmail}`, JSON.stringify(response.data.filterPreferences));
                } else {
                    // console.log("No preferences found in user profile");
                    // If we didn't already load from localStorage and nothing from API, initialize empty
                    if (!localPrefs) {
                        setFilterPreferences([]);
                    }
                }
            } catch (error) {
                console.error('Error fetching preferences from API:', error);
                // console.log("Using localStorage preferences or initializing with empty array");
                
                // If we haven't already loaded from localStorage, initialize empty
                if (!localStorage.getItem(`filterPreferences_${userEmail}`)) {
                    setFilterPreferences([]);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchPreferences();
    }, [userEmail]);
  
    const applyFilterPreference = (index) => {
        if (index < 0 || index >= filterPreferences.length) {
            // console.log("Invalid filter preference index.");
            return;
        }

        const preference = filterPreferences[index];
        setSearchTerm(preference.searchTerm);
        setSelectedClient(preference.selectedClient);
        setSelectedPosition(preference.selectedPosition);
        setSelectedSet(preference.selectedSet);
        setSelectedLocation(preference.selectedLocation);
        setSelectedStatus(preference.selectedStatus);
        setSelectedUser(preference.selectedUser);
    };

    const preferenceOptions = filterPreferences.map((_, index) => ({ value: index, label: `Preference ${index + 1}` }));
    const handlePreferenceChange = selectedOption => {
        setSelectedPreferenceIndex(selectedOption ? selectedOption.value : null);
        if (selectedOption) {
            applyFilterPreference(selectedOption.value);
        } else {
            // Clear all filter preferences here
            setSearchTerm("");
            setSelectedClient(null);
            setSelectedPosition(null);
            setSelectedSet(null);
            setSelectedLocation(null);
            setSelectedStatus(null);
            setSelectedUser(null);
        }
    };
    
    const saveOrUpdateFilterPreference = async () => {
        const newPreference = {
            searchTerm,
            selectedClient,
            selectedPosition,
            selectedTalent,
            selectedSet,
            selectedLocation,
            selectedStatus,
            selectedUser,
        };
    
        let updatedPreferences = [...filterPreferences];
        
        if (selectedPreferenceIndex !== null) {
            // Update an existing preference
            updatedPreferences[selectedPreferenceIndex] = newPreference;
        } else {
            // Save a new preference if there's room
            if (updatedPreferences.length < 3) {
                updatedPreferences.push(newPreference);
            } else {
                alert('You can only save up to 3 filter preferences.');
                return;
            }
        }
    
        try {
            // console.log('Saving filter preferences for user:', userEmail);
            // console.log('Preferences to save:', updatedPreferences);
            
            // Save to localStorage first (will be available even if API fails)
            localStorage.setItem(`filterPreferences_${userEmail}`, JSON.stringify(updatedPreferences));
            
            // Update user profile with new preferences using Auth context
            await updateUserPreferences({
                filterPreferences: updatedPreferences
            });
            
            setFilterPreferences(updatedPreferences); // Update local state
            alert('Filter preferences updated successfully.');
        } catch (error) {
            console.error('Error updating preferences on server:', error);
            // Still update local state since we saved to localStorage
            setFilterPreferences(updatedPreferences);
            alert('Preferences saved locally, but server update failed. Your preferences will be available on this device.');
        }
    };
    
    const DropdownIndicator = props => {
        return (
            components.DropdownIndicator && (
                <components.DropdownIndicator {...props}>
                    <div style={{ width: '16px', margin: '0' }}>
                        <svg version="1.1" id="Isolationsmodus" x="0px" y="0px" viewBox="0 0 322.9 257.78" shapeRendering="geometricPrecision">
                            <path xmlns="http://www.w3.org/2000/svg" d="M208.27,7.37c-4.69-4.79-11.12-7.4-17.72-7.81c-0.4-0.03-0.8-0.05-1.22-0.05l0,0h-35.34h-70.2H31.14h-4.27  C17.33-0.46,7.87,4.51,3.34,13.12C-1.19,21.65-1.3,32.3,4.19,40.47c0.85,1.3,1.74,2.56,2.6,3.82C17.3,59.73,27.77,75.2,38.27,90.64  c12.47,18.33,24.91,36.7,37.39,55.03c3.08,4.53,6.13,9.02,9.21,13.54c3.97,5.86,9.06,10.5,16.08,12.36  c6.87,1.86,14.85,0.93,20.9-2.93c3.79-2.45,6.24-5.12,8.8-8.79c8.06-11.54,15.82-23.3,23.76-34.96  c13.14-19.33,26.25-38.67,39.39-57.96c4.68-6.87,9.32-13.73,14-20.63c4.31-6.31,8.39-12.13,8.2-20.41  C215.85,18.95,213.1,12.34,208.27,7.37z" />
                        </svg>
                    </div>
                </components.DropdownIndicator>
            )
        );
    };

    const handleChange = event => {
        setSearchTerm(event.target.value);
    };

    const fetchBooking = async (bookingId) => {
        try {
            const response = await bookingService.getBookingById(bookingId);
            return response.data;
        } catch (error) {
            handleApiError(error, 'fetching booking details');
            return null;
        }
    };

    const fetchTalent = async (talentId) => {
        try {
            const response = await talentService.getTalentById(talentId);
            return response.data;
        } catch (error) {
            handleApiError(error, 'fetching talent details');
            return null;
        }
    };

    const acceptporposedtalent = async (bookingId) => {
        const userConfirmed = window.confirm("Are you sure you want to confirm?");
        if (userConfirmed) {
            try {
                setLoading(true);
                
                const booking = await fetchBooking(bookingId);
                const talent = await fetchTalent(booking.talentId);
                
                // Update booking status
                await bookingService.updateBookingStatus(bookingId, 'optioned');
                
                // Create task in external system
                await createTask(booking, talent);
                
                alert("Booking updated successfully.");
            } catch (error) {
                handleApiError(error, 'accepting proposed talent');
            } finally {
                setLoading(false);
            }
        } else {
            // console.log("You have cancelled the operation.");
        }
    };

    const declineproposedtalent = async (bookingId) => {
        const userConfirmed = window.confirm("Are you sure you want to reject this order?");
        if (userConfirmed) {
            try {
                setLoading(true);
                
                const booking = await fetchBooking(bookingId);
                const talent = await fetchTalent(booking.talentId);
                
                // Update booking status
                await bookingService.updateBookingStatus(bookingId, 'declined request');
                
                alert("Booking declined successfully.");
            } catch (error) {
                handleApiError(error, 'declining proposed talent');
            } finally {
                setLoading(false);
            }
        } else {
            // console.log("You have cancelled the operation.");
        }
    };

    const sendNotification = async (recipientEmails, subject, content) => {
        try {
            await bookingService.sendEmail({
                to: recipientEmails,
                subject: subject,
                content: content
            });
        } catch (error) {
            console.error('Error sending notification:', error);
        }
    };

    function getWeekNumber(d) {
        // Copy date so don't modify the original
        d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
        
        // Set to nearest Thursday: current date + 4 - current day number
        // Make Sunday's day number 7
        d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
        
        // Get first day of the year
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        
        // Calculate full weeks to nearest Thursday
        const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
        
        return weekNo;
    }

    const createTask = async (booking, talent) => {
        try {
            // console.log('Creating task with auth token:', authToken);
            
            const kw = 'KW' + getWeekNumber(new Date(booking.startDate)) + " ";
            const title = kw + '_' + booking.client.label + '_' + booking.position.label + '_' + 
                new Date(booking.startDate).toLocaleDateString().split('T')[0] + '-' + 
                new Date(booking.endDate).toLocaleDateString().split('T')[0];

            const taskData = {
                task: {
                    title: title,
                    description: booking.description || ' ',
                    details: booking.description || ' ',
                    starts_on: new Date(booking.startDate).toLocaleDateString().split('T')[0],
                    ends_on: new Date(booking.endDate).toLocaleDateString().split('T')[0],
                    rate: talent.Tagessatz,
                    duration_in_time_units: booking.range,
                    currency: "EUR",
                    budget_setting: "set_budget",
                    assignee_id: talent.Lano_ID,
                    rate_type_id: 1,
                    creator_id: 216149,
                    state: "freelancer_assigned",
                    task_cost_centers_attributes: [
                        {
                            cost_center_1_id: booking.costcenter1.id,
                            cost_center_2_id: booking.costcenter2.id
                        }
                    ]
                }
            };

            const response = await bookingService.createLanoTask(taskData, authToken);
            // console.log('Task created successfully:', response);
        } catch (error) {
            console.error('Error creating task:', error);
        }
    };

    const deleteOrder = async (bookingId) => {
        const userConfirmed = window.confirm("Are you sure you want to delete this order?");
        if (userConfirmed) {
            try {
                setLoading(true);
                await bookingService.deleteBooking(bookingId);
                alert("Order deleted successfully.");
                window.location.reload();
            } catch (error) {
                handleApiError(error, 'deleting order');
                alert("There has been an error.");
                window.location.reload();
            } finally {
                setLoading(false);
            }
        }
    };

    // Helper function to get client display 
    const getClientDisplay = (event) => {
        if (!event) return '';
        
        if (event.client) {
            // Handle client as an object with label property
            if (typeof event.client === 'object' && event.client.label) {
                return event.client.label;
            }
            
            // Handle client as a string
            if (typeof event.client === 'string') {
                return event.client;
            }
        }
        
        return '';
    };

    // Helper function to get position display
    const getPositionDisplay = (event) => {
        if (!event) return '';
        
        if (event.position) {
            // Handle position as an object with label property
            if (typeof event.position === 'object' && event.position.label) {
                return event.position.label;
            }
            
            // Handle position as a string
            if (typeof event.position === 'string') {
                return event.position;
            }
        }
        
        return '';
    };

    // Helper function to get talent display
    const getTalentDisplay = (event) => {
        if (!event) return '';

        // Direct talent_name string field (used by calendar view)
        if (event.talent_name && typeof event.talent_name === 'string') {
            return event.talent_name;
        }

        // camelCase variant
        if (event.talentName && typeof event.talentName === 'string') {
            return event.talentName;
        }

        if (event.talent) {
            if (typeof event.talent === 'object') {
                return event.talent.Name || event.talent.name || event.talent.label || '';
            }
            if (typeof event.talent === 'string') {
                return event.talent;
            }
        }

        return '';
    };

    // Helper function to get location display
    const getLocationDisplay = (event) => {
        if (!event) return '';
        
        // Handle location output formatting
        let locationText = '';
        
        // Check if location is an object
        if (event.location && typeof event.location === 'object') {
            // Access name property or short if name doesn't exist
            locationText = event.location.name || event.location.short || '';
        } else if (typeof event.location === 'string') {
            locationText = event.location;
        }
        
        return locationText;
    };

    // Helper function to get location short for filters
    const getLocationShort = (event) => {
        if (!event) return '';
        
        // Handle location output formatting
        let locationText = '';
        
        // Check if location is an object
        if (event.location && typeof event.location === 'object') {
            // Use short property for filters
            locationText = event.location.short || event.location.name || '';
        } else if (typeof event.location === 'string') {
            locationText = event.location;
        }
        
        return locationText;
    };

    // Helper function to get studio display
    const getStudioDisplay = (event) => {
        if (!event) return '';
        
        // Handle studio output formatting
        if (typeof event.studio === 'string') {
            return event.studio;
        } else if (event.studio && typeof event.studio === 'object') {
            return event.studio.name || '';
        }
        
        return '';
    };

    // Helper function for option field
    const getOptionDisplay = (event) => {
        if (!event) return '';
        
        if (event.option) {
            // Handle option as an object with label or value property
            if (typeof event.option === 'object') {
                return event.option.label || event.option.value || '';
            }
            
            // Handle option as a string
            if (typeof event.option === 'string') {
                return event.option;
            }
        }
        
        return '';
    };

    // Helper function for SonstigeInfo field
    const getSonstigeInfoDisplay = (event) => {
        if (!event) return '';
        
        // Check different possible property names for additional info
        if (event.SonstigeInfo) {
            if (typeof event.SonstigeInfo === 'object') {
                return event.SonstigeInfo.text || event.SonstigeInfo.value || '';
            }
            return event.SonstigeInfo;
        }
        
        // Check alternative property names
        if (event.sonstige_info) {
            if (typeof event.sonstige_info === 'object') {
                return event.sonstige_info.text || event.sonstige_info.value || '';
            }
            return event.sonstige_info;
        }
        
        // Check for additional_info property
        if (event.additional_info) {
            if (typeof event.additional_info === 'object') {
                return event.additional_info.text || event.additional_info.value || '';
            }
            return event.additional_info;
        }
        
        return '';
    };

    // Helper function to get created at display
    const getCreatedAtDisplay = (event) => {
        if (!event) return '';
        
        // Check for created_at field
        if (event.created_at) {
            try {
                const date = new Date(event.created_at);
                // Check if date is valid
                if (isNaN(date.getTime())) {
                    // console.warn('Invalid created_at date:', event.created_at);
                    return event.created_at;
                }
                return date.toLocaleDateString();
            } catch (e) {
                console.error('Error parsing created_at date:', e);
                return event.created_at;
            }
        }
        
        // Check for createdAt field (camelCase variant)
        if (event.createdAt) {
            try {
                const date = new Date(event.createdAt);
                // Check if date is valid
                if (isNaN(date.getTime())) {
                    // console.warn('Invalid createdAt date:', event.createdAt);
                    return event.createdAt;
                }
                return date.toLocaleDateString();
            } catch (e) {
                console.error('Error parsing createdAt date:', e);
                return event.createdAt;
            }
        }
        
        // Check for alternative field names that might contain creation date
        if (event.date_created) {
            try {
                const date = new Date(event.date_created);
                if (!isNaN(date.getTime())) {
                    return date.toLocaleDateString();
                }
            } catch (e) {
                console.error('Error parsing date_created:', e);
            }
        }
        
        return '';
    };

    // Create filter options using helper functions and filtering out empty values
    const clientOptions = [...new Set(events.map(event => getClientDisplay(event)).filter(client => client !== ''))]
        .map(client => ({ value: client, label: client }));
    
    const positionOptions = [...new Set(events.map(event => getPositionDisplay(event)).filter(position => position !== ''))]
        .map(position => ({ value: position, label: position }));
    
    const talentOption = [...new Set(events.map(event => getTalentDisplay(event)).filter(talent => talent !== ''))]
        .map(talent => ({ value: talent, label: talent }));
    
    const statusOption = [...new Set(events.map(event => event.status).filter(status => status && typeof status === 'string'))]
        .map(status => ({ value: status, label: status }));
    
    const setOption = [...new Set(events.map(event => event.set).filter(set => set && typeof set === 'string'))]
        .map(set => ({ value: set, label: set }));
    
    const locationOptions = [...new Set(events.map(event => getLocationShort(event)).filter(location => location !== '' && location !== 'OSB'))]
        .map(location => ({ value: location, label: location }));

    // Create user options from multiple user fields in events
    const getUsersFromEvents = () => {
        const users = new Set();
        events.forEach(event => {
            if (event.created_by) users.add(event.created_by);
            if (event.proposed_by) users.add(event.proposed_by);
            if (event.proposedBy) users.add(event.proposedBy);
            if (event.processing_by) users.add(event.processing_by);
        });
        return Array.from(users).filter(user => user && user.trim() !== '')
            .map(user => ({ value: user, label: user }));
    };

    const userOptions = getUsersFromEvents();

    // Update the filteredEvents to include week filtering
    const filteredEvents = events.filter(event => {
        const eventDate = new Date(event.start);
        const { weekStart, weekEnd } = getWeekDates(currentWeek, currentYear);
        
        const isInCurrentWeek = eventDate >= weekStart && eventDate <= weekEnd;
        
        // Log individual event to see structure
        // console.log('Filtering event:', event.id, event.title);
        
        // Helper function to safely check string properties
        const safeStringCheck = (value, searchTerm) => {
            // Check both value and searchTerm are valid strings
            if (!value || typeof value !== 'string' || !searchTerm || typeof searchTerm !== 'string') {
                return false;
            }
            return value.toLowerCase().includes(searchTerm.toLowerCase());
        };
        
        const matchesSearchTerm = !searchTerm || 
            safeStringCheck(event.boktitle, searchTerm) ||
            safeStringCheck(event.set, searchTerm) ||
            safeStringCheck(getTalentDisplay(event), searchTerm) ||
            safeStringCheck(event.status, searchTerm) ||
            safeStringCheck(event.company, searchTerm) ||
            safeStringCheck(getClientDisplay(event), searchTerm) ||
            safeStringCheck(getPositionDisplay(event), searchTerm) ||
            safeStringCheck(event.title, searchTerm);

        const matchesClient = !selectedClient || getClientDisplay(event) === selectedClient.value;
        const matchesPosition = !selectedPosition || getPositionDisplay(event) === selectedPosition.value;
        const matchesTalent = !selectedTalent || getTalentDisplay(event) === selectedTalent.value;
        const matchesSet = !selectedSet || event.set === selectedSet.value;
        const matchesStatus = !selectedStatus || event.status === selectedStatus.value;
        const matchesLocation = !selectedLocation || getLocationShort(event) === selectedLocation.value;
        const matchesUser = !selectedUser || (
            event.created_by === selectedUser.value ||
            event.proposed_by === selectedUser.value ||
            event.proposedBy === selectedUser.value ||
            event.processing_by === selectedUser.value
        );

        // Talent group filter
        const matchesTalentGroup = !selectedTalentGroup || (() => {
            const selectedGroup = talentGroups.find(g => g.id === selectedTalentGroup.value);
            if (selectedGroup && selectedGroup.talentIds && selectedGroup.talentIds.length > 0) {
                // Check if event's talent_id or sql_talent_id is in the group's talentIds
                const eventTalentId = event.talent_id || event.sql_talent_id;
                if (!eventTalentId) return false;
                // Convert to string for comparison since talentIds might be strings or numbers
                return selectedGroup.talentIds.some(id => 
                    String(id) === String(eventTalentId) || 
                    Number(id) === Number(eventTalentId)
                );
            }
            return false;
        })();

        const result = isInCurrentWeek && matchesSearchTerm && matchesClient && matchesPosition && matchesTalent && matchesSet && matchesStatus && matchesLocation && matchesUser && matchesTalentGroup;
        
        // Log detailed filter results
        if (!result) {
            // console.log('Event filtered out:', event.id, 'Reason:',
            //     !isInCurrentWeek ? 'not in current week' :
            //     !matchesSearchTerm ? 'search term' :
            //     !matchesClient ? 'client' :
            //     !matchesPosition ? 'position' :
            //     !matchesTalent ? 'talent' :
            //     !matchesSet ? 'set' :
            //     !matchesStatus ? 'status' :
            //     !matchesLocation ? 'location' : 'unknown');
        }
        
        return result;
    });

    // Log filtered events after filtering
    // console.log('Filtered events count:', filteredEvents.length);

    const handleCheckboxChange = (index) => {
        setSelectedOrders((prevSelected) => ({
            ...prevSelected,
            [index]: !prevSelected[index],
        }));
    };

    const deleteSelectedOrders = async () => {
        const selectedOrderIds = Object.keys(selectedOrders).filter(
            (id) => selectedOrders[id]
        );

        if (selectedOrderIds.length === 0) {
            // console.log("No orders selected.");
            return;
        }

        const userConfirmed = window.confirm("Are you sure you want to delete the selected orders?");
        if (!userConfirmed) return;

        try {
            setLoading(true);
            
            const deletePromises = selectedOrderIds.map(orderId => 
                bookingService.deleteBooking(orderId)
            );

            await Promise.all(deletePromises);
            alert("Selected orders deleted successfully.");
            window.location.reload();
        } catch (error) {
            handleApiError(error, 'deleting selected orders');
            alert("There has been an error.");
        } finally {
            setLoading(false);
        }
    };

    if (error) {
        return (
            <div className="error-message" style={{ 
                padding: '20px', 
                margin: '20px', 
                backgroundColor: '#fff0f0', 
                border: '1px solid #ff8080',
                borderRadius: '5px',
                color: '#c00000'
            }}>
                <h3>An error occurred</h3>
                <p>{error}</p>
                <p>Please try again later or contact support.</p>
                <button 
                    onClick={() => setError(null)} 
                    style={{ 
                        padding: '8px 16px', 
                        backgroundColor: '#121212', 
                        color: 'white', 
                        border: 'none', 
                        cursor: 'pointer' 
                    }}
                >
                    Dismiss
                </button>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="loading-spinner" style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '200px' 
            }}>
                <div className="spinner">
                    <div></div><div></div><div></div><div></div>
                    <div></div><div></div><div></div><div></div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div></div>
            {/* Week Navigation */}
            <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px', 
                marginBottom: '20px',
                padding: '10px',
                backgroundColor: '#f5f5f5'
            }}>
                <button 
                    className="button"
                    onClick={goToPreviousWeek}
                >
                    Previous Week
                </button>
                <div style={{ 
                    padding: '13px 16px', 
                    backgroundColor: '#fff',
                    border: '1px solid #000',
                    fontWeight: '600'
                }}>
                    Week {currentWeek} of {currentYear}
                </div>
                <button 
                    className="button"
                    onClick={goToNextWeek}
                >
                    Next Week
                </button>
                <button 
                    className="button"
                    onClick={goToCurrentWeek}
                >
                    Current Week
                </button>
            </div>

            <div style={{ marginTop: '40px', marginBottom: '60px' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '15px' }}>
                    <input
                        type="text"
                        placeholder="Search"
                        value={searchTerm}
                        onChange={handleChange}
                        className='inputtext search'
                        style={{ flex: 1 }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontWeight: '700', fontSize: '14px', textTransform: 'uppercase' }}>KIND/CGI</span>
                        <label style={{ 
                            position: 'relative', 
                            display: 'inline-block', 
                            width: '60px', 
                            height: '34px',
                            cursor: 'pointer'
                        }}>
                            <input
                                type="checkbox"
                                checked={isKindMode}
                                onChange={() => setIsKindMode(!isKindMode)}
                                style={{ opacity: 0, width: 0, height: 0 }}
                            />
                            <span style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: isKindMode ? '#34C759' : '#ccc',
                                transition: '0.4s',
                                borderRadius: '34px',
                                cursor: 'pointer'
                            }}>
                                <span style={{
                                    position: 'absolute',
                                    content: '""',
                                    height: '26px',
                                    width: '26px',
                                    left: isKindMode ? '30px' : '4px',
                                    bottom: '4px',
                                    backgroundColor: 'white',
                                    transition: '0.4s',
                                    borderRadius: '50%'
                                }} />
                            </span>
                        </label>
                    </div>
                </div>
           
                <div>
                </div>

                <div style={{ marginTop: '40px', marginBottom: '10px', display: 'flex', }}>
                    <Select
                        value={preferenceOptions.find(option => option.value === selectedPreferenceIndex)}
                        onChange={handlePreferenceChange}
                        options={preferenceOptions}
                        isClearable
                        placeholder="Select Preference"
                        styles={customStyles}
                    />
                    <button className='button' onClick={() => saveOrUpdateFilterPreference()}>Save/Update</button>
                </div> 
  
                <div style={{ marginTop: '0px', marginBottom: '60px', display: 'flex', }}>
                    <div style={{ marginRight: '10px' }}>
                        <Select
                            value={selectedClient}
                            onChange={setSelectedClient}
                            options={clientOptions}
                            isClearable
                            placeholder="Filter by Client"
                            components={{ DropdownIndicator }}
                            styles={customStyles}
                        />
                    </div>

                    <Select
                        value={selectedPosition}
                        onChange={setSelectedPosition}
                        options={positionOptions}
                        isClearable
                        placeholder="Filter by Function"
                        components={{ DropdownIndicator }}
                        styles={customStyles}
                    />
                    <Select
                        value={selectedTalent}
                        onChange={setSelectedTalent}
                        options={talentOption}
                        isClearable
                        placeholder="Filter by Talent"
                        components={{ DropdownIndicator }}
                        styles={customStyles}
                    />
                    <Select
                        value={selectedSet}
                        onChange={(value) => {
                            if (!isKindMode) {
                                setSelectedSet(value);
                            }
                        }}
                        options={setOption}
                        isClearable={!isKindMode}
                        isDisabled={isKindMode}
                        placeholder="Filter by Set"
                        components={{ DropdownIndicator }}
                        styles={customStyles}
                    />
                    <Select
                        value={selectedStatus}
                        onChange={setSelectedStatus}
                        options={statusOption}
                        isClearable
                        placeholder="Filter by Status"
                        components={{ DropdownIndicator }}
                        styles={customStyles}
                    />
                    <Select
                        value={selectedLocation}
                        onChange={setSelectedLocation}
                        options={locationOptions}
                        isClearable
                        placeholder="Filter by Location"
                        components={{ DropdownIndicator }}
                        styles={customStyles}
                    />
                    <Select
                        value={selectedUser}
                        onChange={setSelectedUser}
                        options={userOptions}
                        isClearable
                        placeholder="Filter by User"
                        components={{ DropdownIndicator }}
                        styles={customStyles}
                    />
                    <Select
                        value={selectedTalentGroup}
                        onChange={setSelectedTalentGroup}
                        options={talentGroups.map(group => ({
                            value: group.id,
                            label: `${group.name} (${group.talentIds.length} talents)`
                        }))}
                        isClearable
                        placeholder="Filter by Talent Group"
                        components={{ DropdownIndicator }}
                        styles={customStyles}
                    />
                </div>
                <button className="button-red" onClick={deleteSelectedOrders}>
                    Delete Selected Orders
                </button>
            </div>

            <div className={`content-wrapper content-wrapper-talent-list header-list-wrapper`}>
                <div className='list-tasks-header' style={{ display: 'flex' }}>
                    <div style={{ flexBasis: '40px' }}></div>
                    <div style={{ flexBasis: '150px' }}>start/end</div>
                    <div style={{ flexBasis: '100px' }}>set</div>
                    <div style={{ flexBasis: '80px' }}>kpi</div>
                    <div style={{ flexBasis: '100px' }}>fun.</div>
                    <div style={{ flexBasis: '100px' }}>option</div>
                    <div style={{ flexBasis: '100px' }}>talent</div>
                    <div style={{ flexBasis: '100px' }}>status</div>
                    <div style={{ flexBasis: '100px' }}>client</div>
                    <div style={{ flexBasis: '100px' }}>S.info</div>
                    <div style={{ flexBasis: '80px' }}></div>
                </div>
                <div style={{ width: '100%', height: '40px' }}></div>

                {filteredEvents.slice().reverse().map(event => (
                    <div className='list-tasks-item' style={{ display: 'flex' }} key={event.id}>
                        <div style={{ flexBasis: '40px' }} className="custom-checkbox">
                            <input
                                type="checkbox"
                                id={`checkbox-${event.id}`}
                                checked={!!selectedOrders[event.id]}
                                onChange={() => handleCheckboxChange(event.id)}
                            />
                            <label htmlFor={`checkbox-${event.id}`}></label>
                        </div>
                        <div style={{ flexBasis: '150px', whiteSpace: 'pre-wrap' }}>
                            <div>
                                {new Date(event.start).toLocaleDateString()} - 
                                {new Date(event.end).toLocaleDateString()}
                            </div>
                            {getCreatedAtDisplay(event) && (
                                <div style={{ 
                                    fontSize: '0.75rem', 
                                    color: '#666', 
                                    marginTop: '2px',
                                    fontStyle: 'italic'
                                }}>
                                    Created: {getCreatedAtDisplay(event)}
                                </div>
                            )}
                        </div>
                        <div style={{ flexBasis: '100px' }}>{event.set}</div>
                        <div style={{ flexBasis: '80px' }}>
                          {(() => {
                            const kpiValue = event.kpi;
                            if (kpiValue === null || kpiValue === undefined || kpiValue === '') {
                              return '';
                            }
                            
                            // Handle objects (like location objects that might be in the wrong field)
                            if (typeof kpiValue === 'object' && kpiValue !== null) {
                              // console.warn('KPI is an object, using empty string:', kpiValue);
                              return '';
                            }
                            
                            // Convert to number and round to integer
                            const numericValue = parseFloat(kpiValue);
                            if (!isNaN(numericValue)) {
                              return Math.round(numericValue);
                            }
                            
                            // If it's not a valid number, return empty string
                            return '';
                          })()}
                        </div>
                        <div style={{ flexBasis: '100px' }}>{getPositionDisplay(event)}</div>
                        <div style={{ flexBasis: '100px' }}>{getOptionDisplay(event)}</div>
                        <div style={{ flexBasis: '100px' }}>{getTalentDisplay(event)}</div>
                        <div style={{ flexBasis: '100px' }}>{event.status}</div>
                        <div style={{ flexBasis: '100px' }}>{getClientDisplay(event)}</div>
                        <div style={{ flexBasis: '100px' }}>{getSonstigeInfoDisplay(event)}</div>
                        <div style={{ flexBasis: '60px' }}></div>
                        {currentUser && currentUser.role !== 'planning' && (
                            <div id="hideclient" style={{ flexBasis: '80px' }}>
                                <a href={`detail?id=${event.id}`} style={{ color: 'blue', textDecoration: 'underline', paddingRight: 5 }}>
                                    <img src={edit} alt="Edit" className="edit-container" />
                                </a>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ListViewAllBookings;
